import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { BASE_PATH } from '@adlatus-gui/domain/clearing';
import { Configuration, SavedQuery, SavedQueryParamsBuilder } from '@adlatus-gui/domain/clearing-custom';
import { CustomHttpUrlEncodingCodec } from '../../clearing/encoder';

@Injectable({
  providedIn: 'root'
})
export class SavedQueryService {
  protected baseUrl = '';
  public defaultHeaders = new HttpHeaders();
  public configuration = new Configuration();

  constructor(
    protected httpClient: HttpClient,
    @Optional() @Inject(BASE_PATH) basePath: string,
    @Optional() configuration: Configuration
  ) {
    if (basePath) {
      this.baseUrl = basePath;
    }
    if (configuration) {
      this.configuration = configuration;
      this.baseUrl = basePath || configuration.basePath || this.baseUrl;
    }
  }

  createQueryParams(): SavedQueryParamsBuilder {
    return new SavedQueryParamsBuilder();
  }

  public createSavedQuery(
    params: SavedQuery,
    observe: any = 'body',
    reportProgress = false
  ): Observable<SavedQuery> {
    let queryParameters = new HttpParams({ encoder: new CustomHttpUrlEncodingCodec() });

    if (params.name) {
      queryParameters = queryParameters.set('name', params.name);
    }

    if (params.searchQuery) {
      queryParameters = queryParameters.set('searchQuery', params.searchQuery);
    }
    if (params.filters) {
      queryParameters = queryParameters.set('filters', params.filters);
    }

    let headers = this.defaultHeaders;

    // Authentication (basicAuth) required
    if (this.configuration.username || this.configuration.password) {
      headers = headers.set('Authorization', 'Basic ' + btoa(this.configuration.username + ':' + this.configuration.password));
    }

    // Authentication (bearer) required
    if (this.configuration.accessToken) {
      const accessToken = typeof this.configuration.accessToken === 'function'
        ? this.configuration.accessToken()
        : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }

    // Accept header
    const httpHeaderAccepts: string[] = [
      'application/json;charset=utf-8'
    ];
    const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected !== undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    return this.httpClient.post<SavedQuery>(`${this.baseUrl}/saved-query`, null, {
      params: queryParameters,
      withCredentials: this.configuration.withCredentials,
      headers: headers,
      observe: observe as 'body',
      reportProgress: reportProgress
    }).pipe(map(response => response as SavedQuery));
  }

  public retrieveAllSavedQueries(
    observe: any = 'body',
    reportProgress = false
  ): Observable<Array<SavedQuery>> {
    let headers = this.defaultHeaders;

    // Authentication handling
    if (this.configuration.username || this.configuration.password) {
      headers = headers.set('Authorization', 'Basic ' + btoa(this.configuration.username + ':' + this.configuration.password));
    }

    if (this.configuration.accessToken) {
      const accessToken = typeof this.configuration.accessToken === 'function'
        ? this.configuration.accessToken()
        : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }

    // Accept header
    const httpHeaderAccepts: string[] = [
      'application/json;charset=utf-8'
    ];
    const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected !== undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    return this.httpClient.get<Array<SavedQuery>>(`${this.baseUrl}/saved-query`, {
      withCredentials: this.configuration.withCredentials,
      headers: headers,
      observe: observe as 'body',
      reportProgress: reportProgress
    }).pipe(map(response => response as Array<SavedQuery>));
  }

  public deleteSavedQuery(
    id: string,
    observe: any = 'body',
    reportProgress = false
  ): Observable<void> {
    let headers = this.defaultHeaders;

    // Authentication handling
    if (this.configuration.username || this.configuration.password) {
      headers = headers.set('Authorization', 'Basic ' + btoa(this.configuration.username + ':' + this.configuration.password));
    }

    if (this.configuration.accessToken) {
      const accessToken = typeof this.configuration.accessToken === 'function'
        ? this.configuration.accessToken()
        : this.configuration.accessToken;
      headers = headers.set('Authorization', 'Bearer ' + accessToken);
    }

    // Accept header
    const httpHeaderAccepts: string[] = [
      'application/json;charset=utf-8'
    ];
    const httpHeaderAcceptSelected: string | undefined = this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected !== undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    return this.httpClient.delete<void>(`${this.baseUrl}/saved-query/${id}`, {
      withCredentials: this.configuration.withCredentials,
      headers: headers,
      observe: observe as 'body',
      reportProgress: reportProgress
    }).pipe(map(response => response as void));
  }
}
